import { sansPrefixeNiveau } from '../../components/SelecteurContenu.tsx';

/**
 * Fiche de préparation — les données, et ce qu'on en tire.
 *
 * Séparé de l'écran : l'impression en a besoin aussi, et les deux doivent
 * dire la même chose — « Français › Lecture », « 45 min » — sans quoi la
 * feuille et l'écran se contrediraient sur la même fiche.
 */

export interface Objectif {
  text: string;
  curriculumObjectiveId: string | null;
}

export interface Phase {
  id: string;
  title: string;
  duration: number;
  kind: string;
  grouping: string;
  description: string;
}

export interface Piece {
  id: string;
  name: string;
  mimeType: string;
  size: number;
  createdAt: string;
}

export interface Seance {
  id: string;
  title: string;
  material: string;
  comment: string;
  objectives: Objectif[];
  phases: Phase[];
  attachments: Piece[];
}

export interface Dossier {
  sequence: {
    id: string;
    title: string;
    domain: string;
    subdomain: string;
    detail: string;
    objectives: Objectif[];
  };
  sessions: Seance[];
}

export interface ResumeSequence {
  id: string;
  title: string;
  domain: string;
  subdomain: string;
  sessionCount: number;
}

export interface Vocabulaire {
  kinds: string[];
  groupings: string[];
  formats: string[];
  maxBytes: number;
}

/** Le programme de la classe, tel que le rend `/curriculum/tree`. */
export type Programme = Array<{
  id: string;
  name: string;
  subject: string;
  subdomains: Array<{ name: string; objectives: Array<{ id: string; text: string }> }>;
}>;

export interface ObjectifPropose {
  id: string;
  texte: string;
  /** « Lecture › Comprendre un texte » : le groupe de la fenêtre de choix. */
  groupe: string;
}

// ── Le programme ─────────────────────────────────────────────────────────────
/*
 * Même vocabulaire que le cahier journal : le DOMAINE est la grande matière
 * (« Français »), le SOUS-DOMAINE sa subdivision (« Lecture »). Dans l'arbre du
 * programme, le domaine est `subject` et le sous-domaine le `name` du nœud,
 * préfixe de niveau retiré.
 */

const egal = (a: string, b: string) => a.trim().toLowerCase() === b.trim().toLowerCase();

export function domainesDu(programme: Programme): string[] {
  return [...new Set(programme.map((d) => (d.subject ?? '').trim()).filter(Boolean))]
    .sort((a, b) => a.localeCompare(b, 'fr'));
}

export function sousDomainesDe(programme: Programme, domaine: string): string[] {
  if (!domaine.trim()) return [];
  return [...new Set(programme
    .filter((d) => egal(d.subject ?? '', domaine))
    .map((d) => sansPrefixeNiveau(d.name))
    .filter(Boolean))]
    .sort((a, b) => a.localeCompare(b, 'fr'));
}

/**
 * Les objectifs du programme, bornés au sous-domaine quand il est renseigné.
 *
 * Sans cette borne, une séquence de lecture se voyait proposer toute la
 * grammaire : c'est la correction faite dans la v1.
 */
export function objectifsDe(
  programme: Programme,
  domaine: string,
  sousDomaine: string,
): ObjectifPropose[] {
  const propositions: ObjectifPropose[] = [];
  for (const d of programme) {
    if (!egal(d.subject ?? '', domaine)) continue;
    const nom = sansPrefixeNiveau(d.name);
    if (sousDomaine.trim() && !egal(nom, sousDomaine)) continue;
    for (const sd of d.subdomains) {
      for (const o of sd.objectives) {
        if (o.text.trim()) propositions.push({ id: o.id, texte: o.text, groupe: `${nom} › ${sd.name}` });
      }
    }
  }
  return propositions;
}

// ── Libellés ─────────────────────────────────────────────────────────────────

export function libelleDomaine(s: { domain: string; subdomain: string }): string {
  const d = s.domain.trim();
  const sd = s.subdomain.trim();
  if (d && sd) return `${d} › ${sd}`;
  return d || 'Sans domaine';
}

export const pluriel = (n: number, mot: string) => `${n} ${mot}${n > 1 ? 's' : ''}`;

export const dureeDe = (phases: Phase[]) => phases.reduce((n, p) => n + (p.duration || 0), 0);

export function poids(octets: number): string {
  return octets >= 1_048_576
    ? `${(octets / 1_048_576).toFixed(1).replace('.', ',')} Mo`
    : `${Math.max(1, Math.round(octets / 1024))} Ko`;
}

export function echapper(texte: string): string {
  return texte
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/** Un texte de plusieurs lignes, pour un `innerHTML`. */
export const lignes = (texte: string) => echapper(texte).replace(/\n/g, '<br>');

/**
 * Une description, prête à être affichée.
 *
 * Elle a été NETTOYÉE par le serveur à l'écriture — texte échappé, balises
 * triées : c'est lui qui décide de ce qui passe, et l'écran ne refait pas son
 * travail. Seul cas à traiter : une description d'avant le texte enrichi, qui
 * n'est que du texte avec des retours à la ligne. La v1 en garde ; sans balise
 * de bloc, ses lignes se colleraient bout à bout.
 */
export function descriptionAffichable(valeur: string): string {
  if (!valeur.trim()) return '';
  if (!/<[a-z][\s\S]*>/i.test(valeur)) return valeur.replace(/\n/g, '<br>');
  return valeur;
}
